import { apiClient } from "@/api/api";
import { queryOptions } from "@tanstack/react-query";

/**
 * The new schema for your job objects
 */
export interface Job {
  job_id: string;
  product_sku: string;
  job_name: string;
  recurring_or_one_off: "recurring" | "oneoff";
  recurring_frequency: "daily" | "weekly" | "";
  generated_by: "human" | "ai";
  metadata: Record<string, any>;
  agent_id: string; // a.k.a. persona_id
  status: string;
  timezone: string;
  execution_time_in_tz: string;
  created_on: string | null;
  last_run_time: string | null;
  last_run_status: string;
}

/**
 * For creation, we omit the fields the server sets automatically
 */
/* eslint-disable-next-line @typescript-eslint/no-empty-object-type */
export interface CreateJob
  extends Omit<
    Job,
    "job_id" | "created_on" | "last_run_time" | "last_run_status"
  > {}

/**
 * For updating, partial is often enough, but we still need agent_id for the path
 */
export interface UpdateJob extends Partial<Job> {
  job_id: string; // client side usage
  agent_id: string; // we need to know which collection
}

/**
 * GET: Load all jobs for the agent_id
 */
export const getScheduledJobs = async (agentId: string): Promise<Job[]> => {
  const response = await apiClient.get(
    `/api/scheduled_jobs?persona_id=${agentId}`,
  );
  return response.data;
};

/**
 * POST: Create a new job in the agent_id's collection
 */
export const createScheduledJob = async (data: CreateJob) => {
  // data.agent_id is used as persona_id on the server
  const response = await apiClient.post("/api/scheduled_jobs", {
    persona_id: data.agent_id, // so the server recognizes persona_id
    ...data,
  });
  return response.data;
};

/**
 * PATCH: Must call /api/scheduled_jobs/:agent_id/:job_id
 */
export const updateScheduledJob = async (jobData: UpdateJob) => {
  // We need jobData.agent_id for the path
  // We also include jobData minus job_id, agent_id if we want them in the body
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { agent_id, job_id, ...rest } = jobData;
  const response = await apiClient.patch(
    `/api/scheduled_jobs/${jobData.agent_id}/${jobData.job_id}`,
    rest,
  );
  return response.data;
};

/**
 * DELETE: Must call /api/scheduled_jobs/:agent_id/:job_id
 */
export const deleteScheduledJob = async (agentId: string, jobId: string) => {
  await apiClient.delete(`/api/scheduled_jobs/${agentId}/${jobId}`);
};

/**
 * React Query helper
 */
export const jobsQueries = {
  persona: (agentId: string | undefined) =>
    queryOptions({
      queryKey: ["jobs", agentId],
      queryFn: () => getScheduledJobs(agentId ?? ""),
    }),
};
